// <modal-dialog> — wraps a native <dialog>. Markup convention:
//
//   <modal-dialog [alert]>
//     <span data-modal-trigger>…anything clickable…</span>
//     <dialog>…content, with [data-modal-close] on dismiss buttons…</dialog>
//   </modal-dialog>
//
// Without `alert`: Escape and backdrop clicks dismiss. With `alert`: only
// explicit actions ([data-modal-close] or a form submission) dismiss — the
// dialog's cancel event (Escape) is suppressed and backdrop clicks ignored.
// Backdrop detection relies on the dialog itself having no padding (clicks on
// ::backdrop report the dialog as target; content lives in an inner wrapper).

class ModalDialog extends HTMLElement {
	#abort: AbortController | null = null;

	connectedCallback() {
		const dialog = this.querySelector("dialog");
		if (!dialog) return;

		this.#abort?.abort();
		this.#abort = new AbortController();
		const { signal } = this.#abort;
		const alert = this.hasAttribute("alert");

		if (alert) dialog.setAttribute("role", "alertdialog");

		this.addEventListener(
			"click",
			(event) => {
				const target = event.target as HTMLElement;
				const trigger = target.closest<HTMLElement>("[data-modal-trigger]");
				if (trigger && this.#owns(trigger)) {
					dialog.showModal();
					return;
				}
				const close = target.closest<HTMLElement>("[data-modal-close]");
				if (close && this.#owns(close)) dialog.close();
			},
			{ signal },
		);

		// Clicks on ::backdrop hit the dialog element itself
		dialog.addEventListener(
			"click",
			(event) => {
				if (!alert && event.target === dialog) dialog.close();
			},
			{ signal },
		);

		// Escape fires cancel; suppress it in alert mode
		dialog.addEventListener(
			"cancel",
			(event) => {
				if (alert) event.preventDefault();
			},
			{ signal },
		);

		// Never snapshot an open dialog into the Turbo cache
		document.addEventListener("turbo:before-cache", () => dialog.close(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#abort?.abort();
	}

	#owns(el: HTMLElement) {
		return el.closest("modal-dialog") === this;
	}
}

if (!customElements.get("modal-dialog")) {
	customElements.define("modal-dialog", ModalDialog);
}
